// Personas view. Dossier profiles — explicit distinction from login accounts.
import { createPerceptionPanel } from '../components/perception-panel.js';
import type { LocalIdentityBridge } from '../local-identity-types.js';
import { renderHeader } from '../components/header.js';
import type { KnowledgeScope } from '../../api/people-types.js';
import type { ApiClient, ApiError } from '../../api/client.js';
import { h, toast, confirmModal, formModal, type View } from '../components/dom.js';
import { getMobileBridge } from '../../platform/mobile-bridge.js';

export interface PeopleViewHost {
  api(): ApiClient;
  navigate(section: string, scope?: KnowledgeScope): void;
  local?: LocalIdentityBridge;
}

interface PersonaState {
  api: ApiClient;
  container: HTMLElement;
  navigate: PeopleViewHost['navigate'];
  people: import('../../api/people-types.js').Person[];
  selectedId: string | null;
  abort: AbortController | null;
  busy: boolean;
  active: boolean;
  lifetime: AbortController;
  local?: LocalIdentityBridge;
  perception: View | null;
}

export function renderPersonas(host: PeopleViewHost): View {
  const section = document.createElement('section');
  section.className = 'view view-personas';
  const state: PersonaState = {
    api: host.api(),
    container: section,
    navigate: host.navigate,
    people: [],
    selectedId: null,
    abort: null,
    busy: false,
    active: true,
    lifetime: new AbortController(),
    ...(host.local && !getMobileBridge() ? { local: host.local } : {}),
    perception: null,
  };


  const toolbar = h('div', { class: 'toolbar' }, [
    h('button', {
      type: 'button',
      class: 'btn primary',
      'data-action': 'add-person',
      'aria-label': 'Añadir persona',
    }, ['Añadir persona']),
    h('div', { class: 'toolbar-spacer' }, []),
    h('button', {
      type: 'button',
      class: 'btn ghost small',
      'data-action': 'refresh-people',
      hidden: true,
      'aria-label': 'Recargar lista de personas',
    }, ['Reintentar']),
  ]);

  const list = h('ul', { class: 'person-list', 'aria-label': 'Personas' });
  const detail = h('article', { class: 'person-detail', 'aria-live': 'polite' }, []);
  const layout = h('div', { class: 'split-layout' }, [list, detail]);

  section.append(renderHeader('personas'), toolbar, layout);
  section.addEventListener('click', (ev) => onClick(state, host, ev));
  void refresh(state);
  return { el: section, destroy() { state.active = false; state.abort?.abort(); state.lifetime.abort(); state.perception?.destroy?.(); state.perception = null; } };
}

async function refresh(state: PersonaState): Promise<void> {
  if (!state.active) return;
  state.abort?.abort();
  state.abort = new AbortController();
  const signal = state.abort.signal;
  const list = state.container.querySelector<HTMLUListElement>('.person-list');
  const detail = state.container.querySelector<HTMLElement>('.person-detail');
  if (!list || !detail) return;
  state.perception?.destroy?.();
  state.perception = null;
  list.replaceChildren(messageBlock('Cargando personas…'));
  detail.replaceChildren(messageBlock('Selecciona una persona para ver su detalle.'));
  try {
    const res = await state.api.listPeople(signal);
    if (signal.aborted || !state.active) return;
    const retry = state.container.querySelector<HTMLButtonElement>('[data-action="refresh-people"]');
    if (retry) retry.hidden = true;
    state.people = [...res.people].sort((a, b) => a.displayName.localeCompare(b.displayName, 'es'));
    if (state.selectedId && !state.people.some(p => p.id === state.selectedId)) state.selectedId = null;
    if (!state.selectedId && state.people[0]) state.selectedId = state.people[0].id;
    renderList(state);
    renderDetail(state);
  } catch (err) {
    if (signal.aborted || !state.active) return;
    const retry = state.container.querySelector<HTMLButtonElement>('[data-action="refresh-people"]');
    if (retry) retry.hidden = false;
    list.replaceChildren(messageBlock(describeError(err)));
    detail.replaceChildren();
  }
}

function renderList(state: PersonaState): void {
  if (!state.active) return;
  const list = state.container.querySelector<HTMLUListElement>('.person-list');
  if (!list) return;
  list.replaceChildren();
  if (state.people.length === 0) {
    list.append(h('li', { class: 'empty' }, [h('h3', {}, ['Las personas de tu día a día']), h('p', {}, ['Aún no has creado ninguna persona. Empieza con “Añadir persona” o habla con ASIS sobre las personas importantes para ti.']), h('a', { class: 'btn ghost', href: '#/conversar' }, ['Hablar con ASIS'])]));
    return;
  }
  for (const p of state.people) {
    const li = document.createElement('li');
    li.className = 'person-row';
    if (p.id === state.selectedId) li.classList.add('active');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'person-row-btn';
    button.setAttribute('aria-label', `Seleccionar ${p.displayName}`);
    const initial = document.createElement('span');
    initial.className = 'avatar small';
    initial.textContent = (p.displayName.trim().charAt(0).toUpperCase() || '?');
    const meta = document.createElement('span');
    meta.className = 'person-meta';
    const name = document.createElement('span');
    name.className = 'person-name';
    name.textContent = p.displayName;
    const chip = document.createElement('span');
    chip.className = `chip ${p.kind}`;
    chip.textContent = p.kind === 'self' ? 'Tú' : 'Invitado';
    meta.append(name, chip);
    button.append(initial, meta);
    button.addEventListener('click', () => {
      if (!state.active || state.busy) return;
      state.selectedId = p.id;
      renderList(state);
      renderDetail(state);
    });
    li.appendChild(button);
    list.appendChild(li);
  }
}

function renderDetail(state: PersonaState): void {
  if (!state.active) return;
  const detail = state.container.querySelector<HTMLElement>('.person-detail');
  if (!detail) return;
  state.perception?.destroy?.();
  state.perception = null;
  const p = state.people.find(x => x.id === state.selectedId) ?? null;
  if (!p) {
    detail.replaceChildren(messageBlock('Selecciona una persona para ver su detalle.'));
    return;
  }
  detail.replaceChildren();
  const header = h('header', { class: 'detail-header' });
  const initial = document.createElement('span');
  initial.className = 'avatar big';
  initial.textContent = (p.displayName.trim().charAt(0).toUpperCase() || '?');
  const meta = h('div', { class: 'detail-meta' });
  meta.append(
    h('h3', { class: 'detail-name' }, [p.displayName]),
    h('p', { class: 'detail-sub' }, [p.kind === 'self' ? 'Esta es tu propia ficha de persona.' : 'Esta persona no tiene cuenta asociada.']),
    h('ul', { class: 'detail-stats' }, [
      h('li', {}, [`Creada: ${formatDate(p.createdAt)}`]),
    ]),
  );
  header.append(initial, meta);
  const actions = h('div', { class: 'detail-actions' });
  const renameBtn = h('button', { type: 'button', class: 'btn ghost', 'data-action': 'rename-person', 'data-id': p.id }, ['Renombrar']);
  const deleteBtn = h('button', { type: 'button', class: 'btn danger', 'data-action': 'delete-person', 'data-id': p.id }, ['Eliminar']);
  if (p.kind === 'self') {
    renameBtn.textContent = 'Editar Mi cuenta';
    renameBtn.removeAttribute('data-action');
    renameBtn.addEventListener('click', () => state.navigate('perfil'));
    actions.append(renameBtn);
  } else actions.append(renameBtn, deleteBtn);
  header.append(actions);
  detail.append(header);
  const knowledgeButton = h('button', {class:'btn ghost',type:'button'}, ['Abrir conocimiento']);
  knowledgeButton.addEventListener('click', () => { if (state.active) state.navigate('conocimiento', {kind:'person',id:p.id,label:p.displayName}); });
  detail.append(h('section', {class:'person-knowledge'}, [h('h4',{},['Lo que ASIS sabe']), h('p',{class:'muted-text'},['Añade preferencias, información y documentos para este perfil.']), knowledgeButton]));
  if (!getMobileBridge()) {
    state.perception = createPerceptionPanel(state.local, { personId: p.id, displayName: p.displayName });
    detail.append(h('details', {class:'perception-details'}, [h('summary', {}, ['Cara y voz en este Mac']), state.perception.el]));
  }
}

function onClick(state: PersonaState, host: PeopleViewHost, ev: Event): void {
  if (!state.active || state.busy) return;
  const target = ev.target as HTMLElement | null;
  const actionEl = target?.closest<HTMLElement>('[data-action]');
  if (!actionEl) return;
  const action = actionEl.dataset['action'];
  if (action === 'add-person') void promptAdd(state);
  else if (action === 'refresh-people') void refresh(state);
  else if (action === 'rename-person') {
    const id = actionEl.dataset['id'];
    if (id) void promptRename(state, id);
  } else if (action === 'delete-person') {
    const id = actionEl.dataset['id'];
    if (id) void promptDelete(state, id);
  }
}

function promptAdd(state: PersonaState): Promise<void> {
  const fields = [{ name: 'displayName', label: 'Nombre visible', value: '', multiline: false }];
  return runFormModal(state, 'Añadir persona', 'Crear', fields, async (values) => {
    const name = (values['displayName'] ?? '').trim();
    if (!name) throw new Error('Indica un nombre visible.');
    if (name.length > 64) throw new Error('Nombre demasiado largo (máx. 64).');
    await commitAdd(state, name);
  });
}

function promptRename(state: PersonaState, id: string): Promise<void> {
  const target = state.people.find(p => p.id === id);
  if (!target) return Promise.resolve();
  if (target.kind === 'self') {
    toast('Tu propia ficha no se renombra desde aquí. Usa Mi cuenta.', 'info');
    return Promise.resolve();
  }
  const fields = [{ name: 'displayName', label: 'Nuevo nombre', value: target.displayName, multiline: false }];
  return runFormModal(state, 'Renombrar persona', 'Guardar', fields, async (values) => {
    const name = (values['displayName'] ?? '').trim();
    if (!name) throw new Error('Indica un nombre visible.');
    if (name.length > 64) throw new Error('Nombre demasiado largo (máx. 64).');
    await commitRename(state, id, name);
  });
}

async function promptDelete(state: PersonaState, id: string): Promise<void> {
  const target = state.people.find(p => p.id === id);
  if (!target) return;
  if (target.kind === 'self') {
    toast('Tu propia ficha no se puede eliminar.', 'info');
    return;
  }
  const ok = await confirmModal({
    title: 'Eliminar persona',
    body: `Vas a eliminar la ficha de “${target.displayName}”. Se borrará su conocimiento y, si está disponible, su personalización en este Mac. No elimina cuentas ni dispositivos. ¿Continuar?`,
    confirmLabel: 'Eliminar',
    danger: true,
  });
  if (!ok || !state.active) return;
  await commitDelete(state, id);
}

async function runFormModal(state: PersonaState, title: string, submitLabel: string, fields: Array<{ name: string; label: string; value?: string; multiline?: boolean }>, onSave: (values: Record<string, string>) => Promise<void>): Promise<void> {
  await formModal({ title, submitLabel, fields, onSave: async values => { if (!state.active) throw new Error('Esta vista ya se ha cerrado.'); await onSave(values); } });
}

async function commitAdd(state: PersonaState, name: string): Promise<void> {
  if (!state.active || state.busy) return;
  state.busy = true;
  try {
    const res = await state.api.createPerson({ displayName: name }, state.lifetime.signal);
    if (!state.active) return;
    state.people = [...state.people, res.person].sort((a, b) => a.displayName.localeCompare(b.displayName, 'es'));
    state.selectedId = res.person.id;
    renderList(state);
    renderDetail(state);
    toast(`Persona “${res.person.displayName}” creada.`, 'ok');
  } catch (err) {
    if (!state.active || state.lifetime.signal.aborted) return;
    toast(describeError(err), 'error');
    throw err;
  } finally {
    state.busy = false;
  }
}

async function commitRename(state: PersonaState, id: string, name: string): Promise<void> {
  if (!state.active || state.busy) return;
  state.busy = true;
  try {
    const res = await state.api.patchPerson(id, { displayName: name }, state.lifetime.signal);
    if (!state.active) return;
    state.people = state.people.map(p => p.id === id ? res.person : p).sort((a, b) => a.displayName.localeCompare(b.displayName, 'es'));
    renderList(state);
    renderDetail(state);
    toast(`Renombrada a “${res.person.displayName}”.`, 'ok');
  } catch (err) {
    if (!state.active || state.lifetime.signal.aborted) return;
    toast(describeError(err), 'error');
    throw err;
  } finally {
    state.busy = false;
  }
}

async function commitDelete(state: PersonaState, id: string): Promise<void> {
  if (!state.active || state.busy) return;
  state.busy = true;
  try {
    await state.api.deletePerson(id, state.lifetime.signal);
    if (!state.active) return;
    state.people = state.people.filter(p => p.id !== id);
    if (state.selectedId === id) state.selectedId = state.people[0]?.id ?? null;
    renderList(state);
    renderDetail(state);
    if (state.local?.removeProfile) {
      try {
        await state.local.removeProfile({ personId: id, confirm: true });
        if (state.active) toast('Persona y personalización de este Mac eliminadas.', 'ok');
      } catch {
        if (state.active) {
          const warning = h('p', {class:'auth-error', role:'alert'}, ['La ficha del servidor se eliminó, pero no se pudo borrar la personalización de este Mac. Revisa los datos locales antes de volver a usar ese perfil.']);
          state.container.append(warning);
          toast(warning.textContent ?? '', 'error');
        }
      }
    } else toast('Ficha eliminada. Las plantillas locales de otros dispositivos se gestionan en cada dispositivo.', 'info');
  } catch (err) {
    if (!state.active || state.lifetime.signal.aborted) return;
    toast(describeError(err), 'error');
  } finally {
    state.busy = false;
  }
}

function describeError(err: unknown): string {
  const e = err as ApiError | Error;
  if (e && typeof (e as ApiError).message === 'string') return (e as ApiError).message;
  return 'Algo ha fallado. Inténtalo de nuevo.';
}

function formatDate(iso: string): string {
  try { return new Date(iso).toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return iso; }
}

function messageBlock(text: string): HTMLElement {
  const el = document.createElement('li');
  el.className = 'empty-block';
  el.textContent = text;
  return el;
}
